import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mountain, ArrowRight, Check, MapPin, Home, Shield, Star, BarChart3, Users, Globe } from 'lucide-react';
import StateSelector from '../components/StateSelector';
import HimalayanPeaksFooter from '../components/HimalayanPeaksFooter';

const DISTRICTS = [
  // Kashmir Valley
  { id: 'srinagar', name: 'Srinagar', homestays: 420, valleys: ['Dal Lake', 'Mughal Gardens', 'Old City'], altitude: '1,585m', highlight: true, division: 'Kashmir' },
  { id: 'anantnag', name: 'Anantnag', homestays: 350, valleys: ['Pahalgam', 'Betaab Valley', 'Kokernag'], altitude: '1,600–3,800m', highlight: true, division: 'Kashmir' },
  { id: 'baramulla', name: 'Baramulla', homestays: 320, valleys: ['Gulmarg', 'Uri', 'Wular Lake'], altitude: '1,500–4,200m', highlight: true, division: 'Kashmir' },
  { id: 'ganderbal', name: 'Ganderbal', homestays: 250, valleys: ['Sonamarg', 'Sindh Valley', 'Manasbal'], altitude: '1,600–3,800m', highlight: true, division: 'Kashmir' },
  { id: 'budgam', name: 'Budgam', homestays: 180, valleys: ['Yusmarg', 'Doodpathri', 'Chari Sharief'], altitude: '1,600–3,500m', highlight: false, division: 'Kashmir' },
  { id: 'kupwara', name: 'Kupwara', homestays: 160, valleys: ['Lolab Valley', 'Bangus Valley', 'Keran'], altitude: '1,600–4,000m', highlight: false, division: 'Kashmir' },
  { id: 'bandipora', name: 'Bandipora', homestays: 140, valleys: ['Gurez Valley', 'Wular Lake', 'Tragbal'], altitude: '1,600–4,500m', highlight: true, division: 'Kashmir' },
  { id: 'pulwama', name: 'Pulwama', homestays: 120, valleys: ['Saffron Fields', 'Aharbal', 'Shopian'], altitude: '1,600–2,200m', highlight: false, division: 'Kashmir' },
  { id: 'kulgam', name: 'Kulgam', homestays: 80, valleys: ['Aharbal Falls', 'Kounsarnag'], altitude: '1,600–4,000m', highlight: false, division: 'Kashmir' },
  { id: 'shopian', name: 'Shopian', homestays: 60, valleys: ['Apple Orchards', 'Hirpora'], altitude: '1,800–4,200m', highlight: false, division: 'Kashmir' },
  // Jammu Division
  { id: 'doda', name: 'Doda', homestays: 170, valleys: ['Bhaderwah', 'Chinta Valley', 'Padri'], altitude: '1,500–4,500m', highlight: true, division: 'Jammu' },
  { id: 'kishtwar', name: 'Kishtwar', homestays: 110, valleys: ['Kishtwar NP', 'Chenab Gorge', 'Warwan'], altitude: '1,600–6,000m', highlight: false, division: 'Jammu' },
  { id: 'udhampur', name: 'Udhampur', homestays: 100, valleys: ['Patnitop', 'Sanasar', 'Kud'], altitude: '700–2,800m', highlight: false, division: 'Jammu' },
  { id: 'rajouri', name: 'Rajouri', homestays: 80, valleys: ['Pir Panjal', 'Baba Ghulam Shah'], altitude: '900–4,000m', highlight: false, division: 'Jammu' },
  { id: 'poonch', name: 'Poonch', homestays: 70, valleys: ['Poonch Valley', 'Nandansar Lakes'], altitude: '1,000–4,000m', highlight: false, division: 'Jammu' },
  { id: 'reasi', name: 'Reasi', homestays: 50, valleys: ['Vaishno Devi', 'Salal Dam'], altitude: '400–2,500m', highlight: false, division: 'Jammu' },
  { id: 'ramban', name: 'Ramban', homestays: 30, valleys: ['Chenab Valley', 'Neel Nag'], altitude: '700–3,500m', highlight: false, division: 'Jammu' },
  { id: 'jammu', name: 'Jammu', homestays: 25, valleys: ['Bahu Fort', 'Mubarak Mandi'], altitude: '300–1,200m', highlight: false, division: 'Jammu' },
  { id: 'kathua', name: 'Kathua', homestays: 20, valleys: ['Basohli', 'Jasrota Fort'], altitude: '300–2,000m', highlight: false, division: 'Jammu' },
  { id: 'samba', name: 'Samba', homestays: 15, valleys: ['Mansar Lake', 'Purmandal'], altitude: '300–600m', highlight: false, division: 'Jammu' },
];

const TOTAL_HOMESTAYS = DISTRICTS.reduce((sum, d) => sum + d.homestays, 0);

const TIERS = [
  {
    name: 'Registered',
    tagline: 'UT-Licensed Homestays',
    desc: 'Every homestay registered with the J&K Tourism Department, digitized and made discoverable. Basic listing with location, contact, and license verification.',
    icon: Home,
    color: '#8B9DAF',
    features: ['License verification badge', 'Location on district map', 'Basic contact listing', 'Searchable by valley & altitude'],
  },
  {
    name: 'Silver',
    tagline: 'GoHimalayan Quality Assured',
    desc: 'Registered homestays editorially visited and quality-verified by the GoHimalayan team. Verified photos, wazwan-ready kitchens, and traveller-ready listings.',
    icon: Shield,
    color: '#A8B8C8',
    features: ['On-ground quality verification', 'Professional photography', 'AI-curated descriptions', 'Review management', 'Priority in search results'],
  },
  {
    name: 'Gold',
    tagline: 'The GoHimalayan Collective',
    desc: 'Premium partners with full storytelling pages, active marketing, direct booking, and dedicated account management. The jewels of Kashmiri and Dogra hospitality.',
    icon: Star,
    color: '#D4A853',
    features: ['Luxury storytelling page', 'Direct booking integration', 'Social media marketing', 'Revenue analytics dashboard', 'Dedicated account manager', 'Featured in campaigns'],
  },
];

const VALUE_PROPS = [
  { icon: Globe, title: 'Digital Registry', desc: 'Every licensed homestay in J&K — from Dal Lake houseboats to Gurez Valley cottages — searchable, verified, bookable on one platform.' },
  { icon: BarChart3, title: 'Demand Intelligence', desc: 'Real-time traveller search data, seasonal patterns across Amarnath & Vaishno Devi pilgrim seasons, and valley-level occupancy insights.' },
  { icon: Users, title: 'Host Enablement', desc: 'Training, photography, and digital literacy for hosts — turning Kashmiri families into confident micro-entrepreneurs.' },
  { icon: Shield, title: 'Quality Assurance', desc: 'On-ground verification maintaining standards while preserving the warmth of Kashmiri mehmaan-nawazi and Dogra hospitality.' },
];

function JKMap({ activeDistrict, setActiveDistrict }) {
  const districtPaths = {
    // Kashmir Valley
    'kupwara': { d: 'M80 40 L130 30 L170 50 L160 80 L120 85 L85 70 Z', cx: 125, cy: 58 },
    'bandipora': { d: 'M170 50 L220 35 L250 60 L240 90 L200 95 L160 80 Z', cx: 205, cy: 65 },
    'baramulla': { d: 'M50 70 L85 70 L120 85 L130 120 L100 140 L60 120 L40 95 Z', cx: 88, cy: 105 },
    'ganderbal': { d: 'M160 80 L200 95 L240 90 L260 120 L230 140 L190 130 L160 110 L150 90 Z', cx: 205, cy: 110 },
    'srinagar': { d: 'M120 85 L160 80 L160 110 L190 130 L175 155 L140 160 L115 140 L130 120 Z', cx: 152, cy: 125 },
    'budgam': { d: 'M60 120 L100 140 L115 140 L130 120 L120 85 L85 70 L60 90 Z', cx: 95, cy: 115 },
    'pulwama': { d: 'M115 140 L140 160 L175 155 L180 180 L150 200 L120 190 L100 165 Z', cx: 140, cy: 175 },
    'shopian': { d: 'M60 120 L100 140 L115 140 L100 165 L70 170 L45 150 Z', cx: 80, cy: 148 },
    'anantnag': { d: 'M120 190 L150 200 L180 180 L210 195 L220 230 L180 245 L140 230 L120 210 Z', cx: 170, cy: 215 },
    'kulgam': { d: 'M100 165 L120 190 L120 210 L90 220 L65 200 L70 170 Z', cx: 95, cy: 195 },
    // Jammu Division
    'kishtwar': { d: 'M250 60 L310 50 L350 80 L340 130 L300 140 L260 120 L240 90 Z', cx: 295, cy: 95 },
    'doda': { d: 'M260 120 L300 140 L340 130 L350 170 L310 200 L270 185 L230 160 L230 140 Z', cx: 290, cy: 162 },
    'ramban': { d: 'M190 130 L230 140 L230 160 L210 195 L180 180 L175 155 Z', cx: 205, cy: 162 },
    'rajouri': { d: 'M45 150 L70 170 L65 200 L90 220 L80 255 L40 240 L25 200 L30 170 Z', cx: 58, cy: 205 },
    'poonch': { d: 'M25 200 L40 240 L30 270 L10 260 L5 230 Z', cx: 24, cy: 242 },
    'reasi': { d: 'M80 255 L120 270 L140 230 L120 210 L90 220 Z', cx: 112, cy: 240 },
    'udhampur': { d: 'M120 270 L170 285 L180 245 L220 230 L210 260 L160 280 Z', cx: 175, cy: 262 },
    'kathua': { d: 'M170 285 L210 310 L250 300 L230 280 L210 260 L160 280 Z', cx: 210, cy: 292 },
    'jammu': { d: 'M80 255 L120 270 L160 280 L170 285 L140 310 L100 300 L70 280 Z', cx: 120, cy: 285 },
    'samba': { d: 'M140 310 L170 285 L210 310 L190 330 L150 325 Z', cx: 173, cy: 312 },
  };

  return (
    <svg viewBox="0 0 380 350" className="w-full max-w-xl mx-auto" style={{ filter: 'drop-shadow(0 4px 20px rgba(0,0,0,0.3))' }}>
      {Object.entries(districtPaths).map(([id, path]) => {
        const district = DISTRICTS.find(d => d.id === id);
        const isActive = activeDistrict === id;
        const isHighlight = district?.highlight;
        return (
          <g key={id}
            onMouseEnter={() => setActiveDistrict(id)}
            onMouseLeave={() => setActiveDistrict(null)}
            style={{ cursor: 'pointer' }}
          >
            <path
              d={path.d}
              fill={isActive ? '#6B3A3A' : isHighlight ? 'rgba(107,58,58,0.35)' : 'rgba(255,255,255,0.12)'}
              stroke={isActive ? '#D4A853' : 'rgba(255,255,255,0.3)'}
              strokeWidth={isActive ? 2 : 1}
              style={{ transition: 'all 0.3s ease' }}
            />
            <text
              x={path.cx} y={path.cy}
              textAnchor="middle" dominantBaseline="middle"
              fill={isActive ? '#fff' : 'rgba(255,255,255,0.65)'}
              fontSize={isActive ? 10 : 8}
              fontFamily="Georgia, serif"
              style={{ transition: 'all 0.3s ease', pointerEvents: 'none' }}
            >
              {district?.name}
            </text>
            {isActive && (
              <text
                x={path.cx} y={path.cy + 13}
                textAnchor="middle" dominantBaseline="middle"
                fill="#D4A853"
                fontSize={7}
                fontFamily="Georgia, serif"
                style={{ pointerEvents: 'none' }}
              >
                {district?.homestays} homestays
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
}

function AnimatedNumber({ target, duration = 2000 }) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    let start = 0;
    const step = target / (duration / 16);
    const timer = setInterval(() => {
      start += step;
      if (start >= target) { setCount(target); clearInterval(timer); }
      else { setCount(Math.floor(start)); }
    }, 16);
    return () => clearInterval(timer);
  }, [target, duration]);
  return <span>{count.toLocaleString()}+</span>;
}

export default function JKStatePage() {
  const [activeDistrict, setActiveDistrict] = useState(null);
  const activeData = DISTRICTS.find(d => d.id === activeDistrict);

  return (
    <div className="min-h-screen bg-[#141c24]" data-testid="jk-state-page">
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#141c24]/90 backdrop-blur-md border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-white/80 hover:text-white transition-colors">
            <Mountain className="w-5 h-5" />
            <span className="text-sm tracking-[0.15em] uppercase">GoHimalayan</span>
          </Link>
          <StateSelector current="jk" />
        </div>
      </nav>

      {/* Hero */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-16">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1716099934086-d64a79d43297?w=1920&q=80"
            alt="" className="w-full h-full object-cover opacity-60"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#141c24]/40 via-[#141c24]/40 to-[#141c24]/95" />
        </div>
        <div className="relative z-10 text-center px-6 max-w-5xl mx-auto">
          <div className="inline-flex items-center gap-2 border border-white/20 bg-white/5 backdrop-blur-sm px-5 py-2 rounded-full mb-10">
            <div className="w-2 h-2 rounded-full bg-red-400 animate-pulse" />
            <span className="text-white/70 text-[10px] tracking-[0.4em] uppercase">Jammu &amp; Kashmir</span>
          </div>
          <h1 className="text-7xl sm:text-8xl md:text-9xl font-light text-white tracking-tight mb-4"
            style={{ fontFamily: "'Playfair Display', Georgia, serif", textShadow: '0 4px 30px rgba(0,0,0,0.4)' }}>
            <AnimatedNumber target={TOTAL_HOMESTAYS} />
          </h1>
          <p className="text-white/70 text-sm tracking-[0.3em] uppercase mb-12">
            Licensed Homestays &middot; 20 Districts &middot; One Platform
          </p>
          <h2 className="text-2xl sm:text-3xl md:text-4xl text-white font-light italic tracking-tight leading-relaxed max-w-3xl mx-auto mb-14"
            style={{ fontFamily: "'Playfair Display', Georgia, serif", textShadow: '0 2px 20px rgba(0,0,0,0.3)' }}>
            Where chinar leaves turn gold in autumn,<br />and every window opens to a mountain
          </h2>
        </div>
      </section>

      {/* Story */}
      <section className="pt-24 pb-8 px-6 relative" data-testid="story-section">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-[#D4A853] text-[10px] tracking-[0.5em] uppercase mb-6">Jannat-e-Kashmir</p>
          <h2 className="text-2xl sm:text-3xl md:text-4xl text-white font-light italic tracking-tight leading-relaxed mb-8"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            They call it Paradise on Earth.<br />
            The families who live here simply call it home.
          </h2>
          <p className="text-white/50 text-sm sm:text-base leading-relaxed max-w-3xl mx-auto">
            In Kashmir, hospitality is an inheritance — passed down through generations of families 
            who serve noon chai in samovars, who lay out dastarkhwan feasts of wazwan for strangers, 
            who keep kangris warm under their pherans while snow buries the walnut trees outside. 
            From the shikaras of Dal Lake to the shepherds' huts in Gurez, from the saffron fields 
            of Pampore to the pine silence of Yusmarg — every homestay here is a window into a 
            civilization that has turned beauty into a way of living. When travellers choose these homes, 
            they don't just find a bed — they find a family, a story, and a Kashmir that no hotel 
            lobby could ever contain.
          </p>
        </div>
      </section>

      {/* Living Map */}
      <section className="py-24 px-6 relative" data-testid="living-map-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-[#D4A853] text-[10px] tracking-[0.5em] uppercase mb-4">The Living Map</p>
            <h2 className="text-3xl sm:text-4xl text-white font-light italic tracking-tight"
              style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              Kashmir &amp; Jammu. Two souls, one heartbeat.
            </h2>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <JKMap activeDistrict={activeDistrict} setActiveDistrict={setActiveDistrict} />
            </div>
            <div className="space-y-6">
              {activeData ? (
                <div className="transition-all duration-300">
                  <div className="flex items-center gap-2 mb-3">
                    <p className="text-[#D4A853] text-[10px] tracking-[0.4em] uppercase">District</p>
                    <span className="text-white/20 text-[10px]">&middot;</span>
                    <p className="text-white/30 text-[10px] tracking-[0.3em] uppercase">{activeData.division} Division</p>
                  </div>
                  <h3 className="text-4xl text-white font-light italic mb-2" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{activeData.name}</h3>
                  <div className="flex items-baseline gap-3 mb-6">
                    <span className="text-5xl text-white font-light" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{activeData.homestays}</span>
                    <span className="text-white/40 text-sm tracking-wider uppercase">Registered Homestays</span>
                  </div>
                  <div className="space-y-3 mb-6">
                    <div className="flex items-center gap-2">
                      <MapPin className="w-3.5 h-3.5 text-white/40" />
                      <span className="text-white/60 text-sm">{activeData.altitude}</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {activeData.valleys.map(v => (
                        <span key={v} className="text-[10px] tracking-wider uppercase px-3 py-1.5 rounded-full border border-white/15 text-white/55">{v}</span>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center lg:text-left">
                  <p className="text-white/20 text-sm italic mb-6">Hover over a district to explore</p>
                  <div className="grid grid-cols-2 gap-4">
                    {DISTRICTS.filter(d => d.highlight).map(d => (
                      <div key={d.id} className="border border-white/10 rounded-lg p-4 hover:border-white/25 transition-colors cursor-pointer"
                        onMouseEnter={() => setActiveDistrict(d.id)}>
                        <p className="text-white/80 text-sm mb-1">{d.name}</p>
                        <p className="text-2xl text-white font-light" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{d.homestays}</p>
                        <p className="text-white/30 text-[10px] tracking-wider uppercase mt-1">homestays</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="border-t border-white/10 pt-6 mt-8">
                <div className="flex items-center justify-between">
                  <span className="text-white/45 text-xs tracking-wider uppercase">Total across J&K</span>
                  <span className="text-white text-lg font-light" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{TOTAL_HOMESTAYS.toLocaleString()} homestays</span>
                </div>
                <div className="mt-3 h-1 bg-white/10 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-[#6B3A3A] to-[#D4A853] rounded-full" style={{ width: '100%', animation: 'expandBar 2s ease-out' }} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tier Funnel */}
      <section className="py-24 px-6 border-t border-white/10" data-testid="tier-funnel">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-[#D4A853] text-[10px] tracking-[0.5em] uppercase mb-4">The Homestay Index</p>
            <h2 className="text-3xl sm:text-4xl text-white font-light italic tracking-tight mb-4" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Three tiers. One ecosystem.</h2>
            <p className="text-white/50 text-sm max-w-xl mx-auto leading-relaxed">Every homestay begins as Registered. Through quality verification and partnership, they rise through the ranks — from Silver to Gold.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {TIERS.map((tier, i) => (
              <div key={tier.name} className="relative border border-white/10 rounded-xl p-8 hover:border-white/25 transition-all group" style={{ background: `linear-gradient(160deg, ${tier.color}12, transparent)` }}>
                <div className="absolute top-6 right-6 text-white/10 text-6xl font-light" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{i + 1}</div>
                <tier.icon className="w-8 h-8 mb-6" style={{ color: tier.color }} />
                <h3 className="text-xl text-white font-light mb-1" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{tier.name}</h3>
                <p className="text-xs tracking-wider uppercase mb-4" style={{ color: tier.color }}>{tier.tagline}</p>
                <p className="text-white/45 text-sm leading-relaxed mb-6">{tier.desc}</p>
                <ul className="space-y-2.5">
                  {tier.features.map(f => (
                    <li key={f} className="flex items-start gap-2">
                      <Check className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{ color: tier.color }} />
                      <span className="text-white/55 text-sm">{f}</span>
                    </li>
                  ))}
                </ul>
                {i < 2 && <div className="hidden md:flex absolute -right-3 top-1/2 -translate-y-1/2 z-10"><ArrowRight className="w-5 h-5 text-white/10" /></div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* B2G Section */}
      <section className="py-24 px-6 relative overflow-hidden" data-testid="b2g-section">
        <div className="absolute inset-0 bg-gradient-to-b from-[#141c24] via-[#182330] to-[#141c24]" />
        <div className="relative z-10 max-w-6xl mx-auto">
          <div className="text-center mb-20">
            <p className="text-[#D4A853] text-[10px] tracking-[0.5em] uppercase mb-4">For Tourism Departments</p>
            <h2 className="text-3xl sm:text-4xl md:text-5xl text-white font-light italic tracking-tight mb-6" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              We digitize your homestay registry.<br /><span className="text-white/55">You focus on policy.</span>
            </h2>
            <p className="text-white/45 text-sm max-w-2xl mx-auto leading-relaxed">
              GoHimalayan serves as the technology and marketing partner for tourism departments — bringing every licensed homestay online, making them discoverable to millions of travellers, and providing real-time demand intelligence for tourism planning.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 mb-20">
            {VALUE_PROPS.map(prop => (
              <div key={prop.title} className="border border-white/10 rounded-xl p-8 hover:border-white/20 transition-colors">
                <prop.icon className="w-6 h-6 text-[#D4A853] mb-5" />
                <h3 className="text-lg text-white font-light mb-2" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{prop.title}</h3>
                <p className="text-white/45 text-sm leading-relaxed">{prop.desc}</p>
              </div>
            ))}
          </div>
          <div className="border border-[#D4A853]/20 rounded-xl p-10 sm:p-14 text-center" style={{ background: 'linear-gradient(160deg, rgba(212,168,83,0.05), transparent)' }}>
            <p className="text-[#D4A853] text-[10px] tracking-[0.5em] uppercase mb-6">The Partnership Model</p>
            <h3 className="text-2xl sm:text-3xl text-white font-light italic mb-6" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Built to scale. Designed to empower.</h3>
            <div className="max-w-2xl mx-auto space-y-4 mb-10">
              <div className="flex items-center gap-4 text-left">
                <div className="w-8 h-8 rounded-full border border-[#D4A853]/30 flex items-center justify-center flex-shrink-0"><span className="text-[#D4A853] text-xs">1</span></div>
                <p className="text-white/60 text-sm">J&K shares its licensed homestay registry — GoHimalayan digitizes and maps every property</p>
              </div>
              <div className="flex items-center gap-4 text-left">
                <div className="w-8 h-8 rounded-full border border-[#D4A853]/30 flex items-center justify-center flex-shrink-0"><span className="text-[#D4A853] text-xs">2</span></div>
                <p className="text-white/60 text-sm">Travellers discover, compare, and book directly — driving revenue into local communities</p>
              </div>
              <div className="flex items-center gap-4 text-left">
                <div className="w-8 h-8 rounded-full border border-[#D4A853]/30 flex items-center justify-center flex-shrink-0"><span className="text-[#D4A853] text-xs">3</span></div>
                <p className="text-white/60 text-sm">UT receives real-time demand intelligence, occupancy analytics, and tourism planning dashboards</p>
              </div>
            </div>
            <a href="https://wa.me/919871155963?text=Hi%20GoHimalayan%2C%20I%27d%20like%20to%20discuss%20the%20State%20Partnership%20Program%20for%20Jammu%20%26%20Kashmir."
              className="inline-flex items-center gap-3 bg-[#D4A853] text-[#0a0f14] px-8 py-3.5 rounded-full text-xs font-semibold tracking-[0.15em] uppercase hover:bg-[#e0ba6a] transition-colors"
              data-testid="b2g-cta" target="_blank" rel="noopener noreferrer">
              Begin Conversation <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      <HimalayanPeaksFooter accentColor="#D4A853" />

      <style>{`
        @keyframes expandBar {
          from { width: 0; }
          to { width: 100%; }
        }
      `}</style>
    </div>
  );
}
